

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

const ANI_STATE_SINGLETON_KEY = "__aniStateSingleton";

export function initAniState() {
  const existing = globalThis[ANI_STATE_SINGLETON_KEY];
  if (existing) return existing;

  const SEL = {
    observe: ".ani-when-inview, .ani-inview-scope, [data-ani-inview]",
    progress: ".ani-parallax-y, .ani-parallax-x, [data-ani-progress]",
    section: "[data-ani-section]",
    proxScope: ".ani-prox-scope, [data-ani-prox]",
  };

  const DEF = {
    enter: 0.22,
    exit: 0.18,
    rootMargin: "0px 0px -10% 0px",
    metric: "edge", 
    prox: [0.30, 0.60, 0.70, 0.85],
    calmVelocity: 600,         
    dirPulseMS: 160,
    dirDeadbandPX: 2,
  };

  const observed = new Map();
  const observersByRM = new Map();
  let lastDominant = null;

  const lastScore = new WeakMap();
  const EPS = 0.02;
  const DEBOUNCE_MS = 40;
  const lastProxMask = new WeakMap();

  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const isSection     = (el) => el.matches(SEL.section);
  const isProxScope   = (el) => el.matches(SEL.proxScope);
  const isExplicit    = (el) => el.matches(".ani-inview-scope, .ani-when-inview, [data-ani-inview]");
  const inFlag = (el, cfg) => (cfg.section ? el.classList.contains("scrolled-active") : el.classList.contains("is-inview"));

  function readCfg(el) {
    const enter = el.dataset.aniThreshold ? parseFloat(el.dataset.aniThreshold) : DEF.enter;
    const exit = el.dataset.aniExit ? parseFloat(el.dataset.aniExit) : DEF.exit;
    const rootMargin = el.dataset.aniRootmargin || DEF.rootMargin;
    const metric = el.dataset.aniMetric || DEF.metric;
    const once = el.hasAttribute("data-ani-once") || el.classList.contains("ani-once");
    const wantsProgress   = el.matches(SEL.progress);
    let prox = DEF.prox;
    if (el.hasAttribute("data-ani-prox")) {
      const raw = el.getAttribute("data-ani-prox").split(",").map(s => parseFloat(s.trim())).filter(n => !Number.isNaN(n));
      if (raw.length) prox = raw;
    }
    const wantsProximity = isProxScope(el);
    return { enter, exit, rootMargin, metric, once, wantsProgress, wantsProximity, prox, section: isSection(el) };
  }

  function buildThresholds() {
    const steps = 21;
    return Array.from({ length: steps }, (_, i) => +(i / (steps - 1)).toFixed(2));
  }

  function getObserverFor(rootMargin) {
    let io = observersByRM.get(rootMargin);
    if (!io) {
      io = new IntersectionObserver(onIntersect, { root: null, rootMargin, threshold: buildThresholds() });
      observersByRM.set(rootMargin, io);
    }
    return io;
  }

  function getScores(el, metric, ratioFallback, isIntersecting = false) {
    if (metric === "element") {
      const r = Math.max(0, Math.min(1, ratioFallback || 0));
      return { entry: r, exit: r };
    }
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (!isIntersecting || rect.bottom <= 0 || rect.top >= vh) return { entry: 0, exit: 0 };
    const entry = Math.max(0, Math.min(1, rect.bottom / vh));
    const exit  = Math.max(0, Math.min(1, 1 - rect.top / vh));
    return { entry, exit };
  }

  function setInView(el, on) {
    const cfg = observed.get(el);
    if (!cfg) return;
    const deliver = !cfg.section || isExplicit(el);
    if (on) {
      if (cfg.section) el.classList.add("scrolled-active");
      if (deliver && !el.classList.contains("is-inview")) {
        el.classList.add("is-inview");
        if (cfg.once) el.classList.add("was-inview");
        el.dispatchEvent(new CustomEvent("ani:inview-enter", { bubbles: true }));
      }
    } else {
      if (cfg.once && el.classList.contains("was-inview")) return;
      if (cfg.section) el.classList.remove("scrolled-active");
      if (deliver && el.classList.contains("is-inview")) {
        el.classList.remove("is-inview");
        el.dispatchEvent(new CustomEvent("ani:inview-exit", { bubbles: true }));
      }
    }
  }

  function updateProximity(el, entryScore, cfg) {
    if (!cfg.wantsProximity) return;
    const [t30=0.30,t60=0.60,t70=0.70,t85=0.85] = cfg.prox;
    const b0 = entryScore >= t30 ? 1 : 0;
    const b1 = entryScore >= t60 ? 2 : 0;
    const b2 = entryScore >= t70 ? 4 : 0;
    const b3 = entryScore >= t85 ? 8 : 0;
    const mask = b0|b1|b2|b3;
    if (lastProxMask.get(el) === mask) return;
    lastProxMask.set(el, mask);
    el.classList.toggle("is-prox-30", !!b0);
    el.classList.toggle("is-prox-60", !!b1);
    el.classList.toggle("is-prox-70", !!b2);
    el.classList.toggle("is-prox-85", !!b3);
    el.dispatchEvent(new CustomEvent("ani:proximity", { bubbles: true, detail: { score: entryScore, t30, t60, t70, t85, mask } }));
  }

  function onIntersect(entries) {
    let touchedSection = false;
    const now = performance.now();
    entries.forEach((entry) => {
      const el  = entry.target;
      const cfg = observed.get(el);
      if (!cfg) return;

      const { entry: entryScore, exit: exitScore } =
        getScores(el, cfg.metric, entry.intersectionRatio, entry.isIntersecting);

      if (cfg.wantsProgress) el.style.setProperty("--ani-progress", entryScore.toFixed(3));
      updateProximity(el, entryScore, cfg);

      const prevIn = inFlag(el, cfg);
      const prev = lastScore.get(el) || { t: 0, entry: entryScore, exit: exitScore, in: prevIn };
      const dE = Math.abs(entryScore - prev.entry);
      const dX = Math.abs(exitScore  - prev.exit);
      const timeOk = now - prev.t > DEBOUNCE_MS;

      if (dE > EPS || dX > EPS || timeOk) {
        const isIn = inFlag(el, cfg);
        if (!isIn && entryScore >= cfg.enter) {
          setInView(el, true);
          lastScore.set(el, { t: now, entry: entryScore, exit: exitScore, in: true });
        } else if (isIn && exitScore < cfg.exit) {
          setInView(el, false);
          lastScore.set(el, { t: now, entry: entryScore, exit: exitScore, in: false });
        } else {
          lastScore.set(el, { t: now, entry: entryScore, exit: exitScore, in: isIn });
        }
      }

      if (cfg.once && el.classList.contains("was-inview")) {
        const io = getObserverFor(cfg.rootMargin);
        io.unobserve(el);
        observed.delete(el);
      }

      if (cfg.section) touchedSection = true;
    });
    if (touchedSection) updateDominant();
  }

  function updateDominant() {
    let best = null, bestRatio = 0;
    observed.forEach((cfg, el) => {
      if (!cfg.section) return;
      const rect = el.getBoundingClientRect();
      const vh   = window.innerHeight || document.documentElement.clientHeight;
      const vis  = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
      const ratio = vis / vh;
      if (ratio > bestRatio) { bestRatio = ratio; best = el; }
    });
    if (!best || lastDominant === best) return;
    if (lastDominant?.isConnected) lastDominant.classList.remove("is-dominant");
    best.classList.add("is-dominant");
    lastDominant = best;
    document.dispatchEvent(new CustomEvent("ani:dominant-change", { detail: { section: best } }));
  }

  function observeAll(root = document) {
    const set = new Set([
      ...qsa(SEL.observe, root),
      ...qsa(SEL.progress, root),
      ...qsa(SEL.section, root),
      ...qsa(SEL.proxScope, root),
    ]);
    set.forEach((el) => {
      if (observed.has(el)) return;
      const cfg = readCfg(el);
      observed.set(el, cfg);
      const io = getObserverFor(cfg.rootMargin);
      io.observe(el);
    });
  }

  
  const root = document.documentElement;
  const getY = () => window.pageYOffset || document.documentElement.scrollTop || 0;
  let lastY = getY();
  let lastT = performance.now();
  let smoothVel = 0;
  let lastDir = 0;
  let dirPulseTO = null;

  function tick() {
    const y = getY();
    const t = performance.now();
    const dy = y - lastY;
    const dt = Math.max(1, t - lastT);
    const vel = (Math.abs(dy) / dt) * 1000;
    smoothVel = smoothVel ? smoothVel * 0.75 + vel * 0.25 : vel;

    const wasCalm = root.classList.contains("is-calm");
    const nowCalm = smoothVel < DEF.calmVelocity;
    if (wasCalm !== nowCalm) {
      root.classList.toggle("is-calm", nowCalm);
      document.dispatchEvent(new CustomEvent("ani:calm-change", { detail: { isCalm: nowCalm, velocity: smoothVel } }));
    }

    if (Math.abs(dy) > DEF.dirDeadbandPX) {
      const dir = dy > 0 ? 1 : -1;
      if (dir !== lastDir) {
        root.setAttribute("data-scroll-dir", dir > 0 ? "down" : "up");
        root.classList.add("is-dir-change");
        document.dispatchEvent(new CustomEvent("ani:direction-change", { detail: { dir: dir > 0 ? "down" : "up" } }));
        if (dirPulseTO) clearTimeout(dirPulseTO);
        dirPulseTO = setTimeout(() => root.classList.remove("is-dir-change"), DEF.dirPulseMS);
        lastDir = dir;
      }
    }

    lastY = y;
    lastT = t;
  }

  
  if (!globalThis.__aniSensorsLoop) {
    globalThis.__aniSensorsLoop = true;
    gsap.ticker.add(() => { try { tick(); } catch {} });
  }

  
  observeAll(document);
  requestAnimationFrame(() => {
    observed.forEach((cfg, el) => {
      if (!cfg.wantsProgress) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const p = Math.max(0, Math.min(1, rect.bottom / vh));
      el.style.setProperty("--ani-progress", p.toFixed(3));
    });
  });

  
  const api = {
    refresh(root = document) {
      observeAll(root);
      try { ScrollTrigger.refresh(); } catch {}
    },
    setInView(el, on) { setInView(el, on); },
    configDefault(overrides = {}) {
      const { enter, exit, rootMargin, metric, prox, calmVelocity, dirPulseMS, dirDeadbandPX } = overrides;
      if (typeof enter === "number") DEF.enter = enter;
      if (typeof exit === "number") DEF.exit = exit;
      if (typeof rootMargin === "string") DEF.rootMargin = rootMargin;
      if (typeof metric === "string") DEF.metric = metric;
      if (Array.isArray(prox)) DEF.prox = prox;
      if (typeof calmVelocity === "number") DEF.calmVelocity = calmVelocity;
      if (typeof dirPulseMS === "number") DEF.dirPulseMS = dirPulseMS;
      if (typeof dirDeadbandPX === "number") DEF.dirDeadbandPX = dirDeadbandPX;

      observersByRM.forEach(io => io.disconnect());
      observersByRM.clear();
      observed.forEach((cfg, el) => {
        const io = getObserverFor(cfg.rootMargin);
        io.observe(el);
      });
      try { ScrollTrigger.refresh(); } catch {}
    },
  };
  globalThis[ANI_STATE_SINGLETON_KEY] = api;
  return api;
}
